import type { Lang } from '../i18n'
import type { SoundId } from '../core/audio'

export interface DriftLine {
  text: Record<Lang, string>
  /** Silence after the line, in seconds. The pauses are the content. */
  pause: number
}

export interface DriftScript {
  id: string
  title: Record<Lang, string>
  blurb: Record<Lang, string>
  bed: SoundId
  minutes: number
  lines: DriftLine[]
}

/**
 * Written to a few rules learned the hard way:
 *
 * - Second person, present tense, no plot. A story with a plot keeps you awake
 *   to find out what happens.
 * - Nothing is ever at risk. No edges, no heights, no water you could fall into.
 * - Never ask the listener to remember, imagine hard, or decide anything.
 * - Someone else is always doing the work — driving, keeping watch, writing it
 *   down. The point is permission to stop holding the night up yourself.
 * - Long pauses. If it feels too slow when you read it, it is about right.
 */
export const DRIFT_SCRIPTS: DriftScript[] = [
  {
    id: 'train',
    bed: 'brown',
    minutes: 11,
    title: { en: 'The night train', fa: 'قطار شب' },
    blurb: {
      en: 'Someone else is driving. You have nowhere to be until morning.',
      fa: 'یکی دیگر می‌راند. تا صبح هیچ‌جا کاری نداری.',
    },
    lines: [
      {
        text: {
          en: 'You are on a train, and it is already moving.',
          fa: 'در قطاری هستی، و قطار از قبل حرکت کرده.',
        },
        pause: 5,
      },
      {
        text: {
          en: 'You did not have to catch it. It was always going to wait for you.',
          fa: 'لازم نبود به آن برسی. قرار بود منتظرت بماند.',
        },
        pause: 6,
      },
      {
        text: {
          en: 'The compartment is warm. The lights are turned down to almost nothing.',
          fa: 'کوپه گرم است. چراغ‌ها تا نزدیک خاموشی کم شده‌اند.',
        },
        pause: 6,
      },
      {
        text: {
          en: 'Outside the window it is black, and every so often a single light goes by.',
          fa: 'بیرون پنجره سیاه است، و هر از گاهی یک نور تنها رد می‌شود.',
        },
        pause: 7,
      },
      {
        text: {
          en: 'A farmhouse. A crossing. A lamp on a pole, and then black again.',
          fa: 'یک خانهٔ روستایی. یک گذرگاه. چراغی روی تیر، و دوباره سیاهی.',
        },
        pause: 8,
      },
      {
        text: {
          en: 'The floor moves under you in a slow, steady way.',
          fa: 'کف زیر پایت آرام و یکنواخت تکان می‌خورد.',
        },
        pause: 7,
      },
      {
        text: {
          en: 'Somewhere at the front, a driver you will never meet is watching the track.',
          fa: 'جایی در جلو، راننده‌ای که هرگز نمی‌بینی حواسش به ریل است.',
        },
        pause: 7,
      },
      {
        text: {
          en: 'That is their whole job tonight. It is not yours.',
          fa: 'کار امشب او همین است. کار تو نیست.',
        },
        pause: 8,
      },
      {
        text: {
          en: 'You are not lost. You are in transit. Those are different things.',
          fa: 'گم نشده‌ای. در راهی. این‌ها دو چیز متفاوت‌اند.',
        },
        pause: 8,
      },
      {
        text: {
          en: 'The seat is holding your weight. Let it take more of it.',
          fa: 'صندلی وزنت را نگه داشته. بگذار بیشترش را بگیرد.',
        },
        pause: 9,
      },
      {
        text: {
          en: 'Your head is heavier than it was a minute ago.',
          fa: 'سرت از یک دقیقه پیش سنگین‌تر است.',
        },
        pause: 9,
      },
      {
        text: {
          en: 'The train will keep going whether you stay awake for it or not.',
          fa: 'قطار می‌رود، چه برایش بیدار بمانی چه نمانی.',
        },
        pause: 10,
      },
      {
        text: { en: 'Nothing arrives until morning.', fa: 'تا صبح هیچ‌چیز نمی‌رسد.' },
        pause: 12,
      },
      {
        text: {
          en: 'Black window. Warm floor. Someone else driving.',
          fa: 'پنجرهٔ سیاه. کف گرم. یکی دیگر پشت فرمان.',
        },
        pause: 14,
      },
    ],
  },

  {
    id: 'house',
    bed: 'rain',
    minutes: 12,
    title: { en: 'The house in the rain', fa: 'خانه در باران' },
    blurb: {
      en: 'A storm outside, and none of it can reach you.',
      fa: 'توفانی بیرون، و هیچ‌کدامش به تو نمی‌رسد.',
    },
    lines: [
      {
        text: {
          en: 'There is a small house, and you are inside it, and the door is already locked.',
          fa: 'خانهٔ کوچکی هست، و تو داخل آنی، و در از قبل قفل است.',
        },
        pause: 6,
      },
      {
        text: {
          en: 'It has been raining for hours. It will rain all night.',
          fa: 'ساعت‌هاست باران می‌بارد. تمام شب خواهد بارید.',
        },
        pause: 6,
      },
      {
        text: {
          en: 'You can hear it on the roof, just above you. A wide, even sound.',
          fa: 'صدایش را روی سقف می‌شنوی، درست بالای سرت. صدایی پهن و یکنواخت.',
        },
        pause: 7,
      },
      {
        text: {
          en: 'The window has water running down it in threads.',
          fa: 'روی شیشه، آب مثل نخ پایین می‌آید.',
        },
        pause: 7,
      },
      {
        text: {
          en: 'None of it comes in. That is what a roof is for.',
          fa: 'هیچ‌کدامش تو نمی‌آید. سقف برای همین است.',
        },
        pause: 8,
      },
      {
        text: {
          en: 'The room is dark except for one low lamp in the corner, and even that is dimming.',
          fa: 'اتاق تاریک است جز یک چراغ کم‌نور در گوشه، و همان هم دارد کم‌تر می‌شود.',
        },
        pause: 8,
      },
      {
        text: {
          en: 'There is a blanket over you and it is heavier than you expected.',
          fa: 'پتویی رویت هست و از آنچه فکر می‌کردی سنگین‌تر است.',
        },
        pause: 8,
      },
      {
        text: {
          en: 'Nobody is coming to the door. Nobody needs anything from you tonight.',
          fa: 'کسی پشت در نمی‌آید. امشب کسی از تو چیزی نمی‌خواهد.',
        },
        pause: 9,
      },
      {
        text: {
          en: 'The storm is doing all the work outside, and you are doing none of it.',
          fa: 'توفان بیرون تمام کار را می‌کند، و تو هیچ‌کدامش را.',
        },
        pause: 9,
      },
      {
        text: {
          en: 'Your shoulders are lower than they were. Let them go further.',
          fa: 'شانه‌هایت پایین‌تر از قبل‌اند. بگذار بیشتر پایین بروند.',
        },
        pause: 10,
      },
      {
        text: {
          en: 'The rain does not need you to listen to it. It carries on either way.',
          fa: 'باران لازم ندارد گوش بدهی. به هر حال ادامه می‌دهد.',
        },
        pause: 11,
      },
      {
        text: { en: 'Warm room. Locked door. Rain all night.', fa: 'اتاق گرم. در قفل. باران تمام شب.' },
        pause: 14,
      },
    ],
  },

  {
    id: 'tablets',
    bed: 'drone',
    minutes: 10,
    title: { en: 'The room of tablets', fa: 'اتاق لوح‌ها' },
    blurb: {
      en: 'Someone writes tonight down so that you do not have to carry it.',
      fa: 'یکی امشب را می‌نویسد تا تو مجبور نباشی حملش کنی.',
    },
    lines: [
      {
        text: {
          en: 'There is a long low room, lit by oil, lined with clay tablets.',
          fa: 'اتاقی دراز و کوتاه هست، روشن با روغن، پوشیده از لوح‌های گلی.',
        },
        pause: 6,
      },
      {
        text: {
          en: 'Every tablet is one night. Somebody else’s, mostly. Thousands of them.',
          fa: 'هر لوح یک شب است. بیشترشان مال کسان دیگر. هزاران تا.',
        },
        pause: 7,
      },
      {
        text: {
          en: 'Someone is sitting at the end of the room, writing. He is not in a hurry.',
          fa: 'کسی ته اتاق نشسته و می‌نویسد. عجله ندارد.',
        },
        pause: 7,
      },
      {
        text: {
          en: 'He is writing tonight down. The date. The hour you woke. That you got through it.',
          fa: 'دارد امشب را می‌نویسد. تاریخ را. ساعتی که بیدار شدی. اینکه از آن رد شدی.',
        },
        pause: 8,
      },
      {
        text: {
          en: 'He does not need your help to do it, and he does not want your commentary.',
          fa: 'برای این کار به کمکت احتیاج ندارد، و توضیحاتت را هم نمی‌خواهد.',
        },
        pause: 8,
      },
      {
        text: {
          en: 'This is the part you can put down: the remembering. He has it.',
          fa: 'این همان چیزی است که می‌توانی زمین بگذاری: به‌یاد‌سپردن. او دارَدش.',
        },
        pause: 9,
      },
      {
        text: {
          en: 'On the shelves there are hundreds of nights that felt exactly like this one.',
          fa: 'روی قفسه‌ها صدها شب هست که دقیقاً حس امشب را داشتند.',
        },
        pause: 9,
      },
      {
        text: {
          en: 'Every one of them ended. That is why they are on a shelf.',
          fa: 'همه‌شان تمام شدند. برای همین روی قفسه‌اند.',
        },
        pause: 10,
      },
      {
        text: {
          en: 'The stylus keeps moving. Slow. Even. Somebody has the record.',
          fa: 'قلم همچنان حرکت می‌کند. آهسته. یکنواخت. کسی دفتر را دارد.',
        },
        pause: 11,
      },
      {
        text: { en: 'You can stop holding it now.', fa: 'حالا می‌توانی نگهش نداری.' },
        pause: 14,
      },
    ],
  },

  {
    id: 'down',
    bed: 'hush',
    minutes: 9,
    title: { en: 'Down', fa: 'پایین' },
    blurb: {
      en: 'A body scan, going downward. No imagination required.',
      fa: 'مرور بدن، رو به پایین. نیازی به تخیل نیست.',
    },
    lines: [
      {
        text: {
          en: 'Nothing to picture in this one. Just notice, and move down.',
          fa: 'در این یکی چیزی برای تصور کردن نیست. فقط توجه کن و پایین برو.',
        },
        pause: 5,
      },
      {
        text: {
          en: 'Your forehead. Let it be wider than it is.',
          fa: 'پیشانی‌ات. بگذار پهن‌تر از آنچه هست باشد.',
        },
        pause: 7,
      },
      {
        text: { en: 'Your jaw. Let the teeth come apart.', fa: 'فکت. بگذار دندان‌ها از هم باز شوند.' },
        pause: 7,
      },
      {
        text: {
          en: 'Your throat. Nothing has to be said tonight.',
          fa: 'گلویت. امشب چیزی لازم نیست گفته شود.',
        },
        pause: 8,
      },
      {
        text: {
          en: 'Your shoulders. They have been up near your ears for weeks.',
          fa: 'شانه‌هایت. هفته‌هاست نزدیک گوش‌هایت بالا مانده‌اند.',
        },
        pause: 8,
      },
      {
        text: {
          en: 'Your chest. Do not make it breathe. It already knows how.',
          fa: 'قفسهٔ سینه‌ات. مجبورش نکن نفس بکشد. خودش بلد است.',
        },
        pause: 9,
      },
      {
        text: {
          en: 'Your stomach. Let it be soft, even if that feels unsafe.',
          fa: 'شکمت. بگذار نرم باشد، حتی اگر ناامن به نظر برسد.',
        },
        pause: 9,
      },
      {
        text: { en: 'Your hands. Nothing to hold.', fa: 'دست‌هایت. چیزی برای گرفتن نیست.' },
        pause: 9,
      },
      {
        text: {
          en: 'Your legs. Heavy. Going nowhere.',
          fa: 'پاهایت. سنگین. جایی نمی‌روند.',
        },
        pause: 10,
      },
      {
        text: {
          en: 'Your feet. The furthest point from your thoughts.',
          fa: 'کف پاهایت. دورترین نقطه از فکرهایت.',
        },
        pause: 11,
      },
      {
        text: {
          en: 'Everything above them can stay exactly where it is.',
          fa: 'هرچه بالای آن‌هاست می‌تواند دقیقاً همان‌جا بماند.',
        },
        pause: 14,
      },
    ],
  },
]

export function scriptById(id: string): DriftScript {
  return DRIFT_SCRIPTS.find((s) => s.id === id) ?? DRIFT_SCRIPTS[0]!
}
